import React from 'react';

/**
 * MicroYearBars
 * - Tiny year-by-year bar chart used in ResearcherCard footer.
 * - Componentized for reuse. Accepts either:
 *   1) items: [{ label: string|number, value: number, muted?: boolean }]  // easy mode
 *      - Typically label = year, value = count.
 *   2) item: original researcher object with various possible year count shapes
 *      (pub_year_counts | year_counts | yearly_publications | publications_by_year | yearly)
 * - maxBars: number of bars to show (latest window if more data provided)
 */
export default function MicroYearBars({ items, item, maxBars = 12 }) {
  // Normalize easy mode input first: items = [{label, value}]
  const normalizeFromItems = () => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const out = [];
    try {
      items.forEach((d) => {
        // Support both {year, count} and {label, value}
        const hasYearCount = d && (typeof d.year !== 'undefined' || typeof d.count !== 'undefined');
        if (hasYearCount) {
          const y = Number(d.year);
          const v = Number(d.count);
          if (Number.isFinite(v) && v >= 0) {
            if (Number.isFinite(y) && y > 1900 && y < 2100) out.push({ year: y, count: v });
            else out.push({ year: d.year, count: v });
          }
          return;
        }
        // Fallback to {label, value}
        const yRaw = d?.label;
        const y = Number(yRaw);
        const v = Number(d?.value);
        if (Number.isFinite(v) && v >= 0) {
          if (Number.isFinite(y) && y > 1900 && y < 2100) out.push({ year: y, count: v });
          else out.push({ year: yRaw, count: v });
        }
      });
    } catch (e) { }
    // If labels are numeric years, sort by year asc; otherwise keep input order
    const allYearsNumeric = out.every(r => typeof r.year === 'number');
    return allYearsNumeric ? out.sort((a, b) => a.year - b.year) : out;
  };

  // Legacy normalization from item object
  const normalizeFromItem = () => {
    const out = [];
    try {
      const cand = item?.pub_year_counts || item?.year_counts || item?.yearly_publications || item?.publications_by_year || item?.yearly;
      if (!cand) return out;
      if (Array.isArray(cand)) {
        cand.forEach((d) => {
          const y = Number(d?.year ?? d?.y ?? d?.key);
          const v = Number(d?.count ?? d?.value ?? d?.v);
          if (Number.isFinite(y) && y > 1900 && y < 2100 && Number.isFinite(v) && v >= 0) out.push({ year: y, count: v });
        });
      } else if (typeof cand === 'object') {
        Object.keys(cand).forEach((k) => {
          const y = Number(k);
          const v = Number(cand[k]);
          if (Number.isFinite(y) && y > 1900 && y < 2100 && Number.isFinite(v) && v >= 0) out.push({ year: y, count: v });
        });
      }
    } catch (e) { }
    return out.sort((a, b) => a.year - b.year);
  };

  let rows = normalizeFromItems();
  if (!rows || rows.length === 0) rows = normalizeFromItem();

  // Windowing: last maxBars entries (by given order)
  const lastIdx = rows.length - 1;
  const startIdx = Math.max(0, rows.length - maxBars);
  const windowRows = rows.slice(startIdx, lastIdx + 1);
  const maxVal = Math.max(1, ...windowRows.map(r => r.count || 0));

  // Determine min/max labels for edges
  const first = windowRows[0];
  const last = windowRows[windowRows.length - 1];
  const leftLabel = (first && first.year != null) ? String(first.year) : '';
  const rightLabel = (last && last.year != null) ? String(last.year) : '';

  return (
    <div className="mt-2 flex items-end justify-center gap-0 text-[12px] text-[#8D9AAF] leading-[140%]">
      <div className="h-[20px] flex items-end pb-0">{leftLabel}</div>
      <div className="h-[20px] flex items-end gap-[3px] px-2 scale-75 origin-bottom">
        {windowRows.map((r, i) => {
          const h = Math.max(2, Math.round(((r.count || 0) / maxVal) * 18));
          return <span key={(r.year ?? i) + ':' + i} className="w-[3px] bg-[#8D9AAF] rounded-[1px]" style={{ height: `${h}px`, opacity: 0.8 }} />;
        })}
      </div>
      <div className="h-[20px] flex items-end pb-0">{rightLabel}</div>
    </div>
  );
}
